import { useNetwork } from '../context/NetworkContext';
import { cn } from '../lib/utils';

interface ConnectionIndicatorProps {
  className?: string;
}

/**
 * Small status dot indicating network connection state.
 * - Green: online (good connection)
 * - Yellow: slow connection
 * - Red with pulse: offline
 *
 * Universal status semantics (green/yellow/red), not MD3 role tokens — the
 * brand palette doesn't carry a "success" hue, and status dots need meaning
 * independent of brand color the way error/warning colors conventionally do.
 */
export function ConnectionIndicator({ className }: ConnectionIndicatorProps) {
  const { state } = useNetwork();

  return (
    <div
      className={cn(
        'relative h-2 w-2 rounded-full transition-colors',
        state === 'online' && 'bg-green-500',
        state === 'slow' && 'bg-yellow-400',
        state === 'offline' && 'bg-red-500',
        className
      )}
      role="status"
      aria-label={`Connection status: ${state}`}
    >
      {/* Pulse animation for offline state */}
      {state === 'offline' && (
        <span className="absolute inset-0 animate-pulse-slow rounded-full bg-red-500 opacity-75" />
      )}
    </div>
  );
}
